import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Button, Stack, type SxProps, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { getPosts } from '@/api/appstore.api';
import type { TableColumnProps } from '@/interfaces/component/table.interface';
import { type AppStore, Post } from '@/interfaces/service/appstore/appstore.interface';
import { baseAppStorePostFilter, useAppStoreStore } from '@/stores/appstore.store';
import { setRequestPostParams } from '@/services/appstore.service';
import { REVIEW_ROUTES } from '@/constants/path.constants';
import { getChannelKoreanNameById, getChannelLogoSrcById } from '@/utils/appstore.util';
import { getTimeOffset } from '@/utils/datetime.util';
import FlexBox from '@/components/FlexBox';
import ReviewScore from '@/views/appstore/components/ReviewScore';
import DashboardWatingDataFallback from '@/views/appstore/dashboard/components/DashboardWatingDataFallback';
import theme from '@/app/theme';

type ReviewTableColumnType = 'SCORE' | 'CHANNEL' | 'CONTENTS';
const tableProps: Record<ReviewTableColumnType, TableColumnProps> = {
  SCORE: {
    label: '별점',
    size: 1.4
  },
  CHANNEL: {
    label: '채널',
    size: 1.4
  },
  CONTENTS: {
    label: '내용',
    size: 'grow'
  }
};

const Row = ({ columDataList, rowStyle = {} }: { columDataList: TableColumnProps[]; rowStyle?: SxProps }) => {
  return (
    <Grid container sx={rowStyle}>
      {columDataList.map(({ label, children, size }, i) => (
        <Grid
          key={i}
          size={size ?? 'grow'}
          sx={{
            display: 'flex'
          }}
        >
          {label ? (
            <Typography variant="body2" paddingX="16px" textAlign={size === 'grow' ? 'left' : 'center'} fontWeight={600} style={{ width: '100%' }}>
              {label}
            </Typography>
          ) : (
            children
          )}
        </Grid>
      ))}
    </Grid>
  );
};

const getPostRowData = (post: Post): TableColumnProps[] => {
  const { score, channelId, content, enhancedContent } = post;

  const scoreColumn = {
    children: (
      <FlexBox style={{ width: '100%' }}>
        <ReviewScore score={score} />
      </FlexBox>
    ),
    size: tableProps.SCORE.size
  };
  const channelColumn = {
    children: (
      <FlexBox style={{ width: '100%' }}>
        <Image src={getChannelLogoSrcById(channelId)} alt={getChannelKoreanNameById(channelId)} width={20} height={20} />
      </FlexBox>
    ),
    size: tableProps.CHANNEL.size
  };
  const contentColumn = {
    children: (
      <FlexBox
        style={{
          width: '100%',
          flexDirection: 'column',
          alignItems: 'start',
          marginX: '16px',
          gap: '4px',
          overflow: 'hidden'
        }}
      >
        <Typography
          variant="body1"
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }}
        >
          {enhancedContent || content}
        </Typography>
        <Typography variant="body2" color={theme.palette.gray[400]}>
          {getTimeOffset(post.authorDtm)}
        </Typography>
      </FlexBox>
    ),
    size: tableProps.CONTENTS.size
  };

  return [scoreColumn, channelColumn, contentColumn];
};

const LatestReviews = ({ currentAppStore }: { currentAppStore: AppStore }) => {
  const router = useRouter();
  const { setAppStorePostConditions } = useAppStoreStore((state) => state);

  const [latestPosts, setLatestPosts] = useState<Post[]>();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const listHeaders: TableColumnProps[] = [tableProps.SCORE, tableProps.CHANNEL, tableProps.CONTENTS];

  useEffect(() => {
    const getLatestPosts = async () => {
      const params = setRequestPostParams({
        ...currentAppStore,
        filter: baseAppStorePostFilter,
        sortOption: 'LATEST'
      });

      const res = await getPosts(params);
      if (res) {
        setLatestPosts(res.data ?? []);
      }
      setIsDataLoaded(true);
    };
    getLatestPosts();
  }, []);

  if ((latestPosts ?? []).length === 0 || !isDataLoaded) {
    return <DashboardWatingDataFallback message="최근 작성된 리뷰를 불러오고 있습니다." />;
  } else {
    return (
      <>
        <Stack marginY={'24px'} width="100%" flex={1}>
          <Row
            columDataList={listHeaders}
            rowStyle={{
              backgroundColor: theme.palette.gray[50],
              border: `1px solid ${theme.palette.gray[100]}`,
              borderRightWidth: 0,
              borderLeftWidth: 0,
              paddingY: '8px'
            }}
          />
          {latestPosts?.map((post, i) => (
            <Row
              key={i}
              columDataList={getPostRowData(post)}
              rowStyle={{
                width: '100%',
                borderBottom: `1px solid ${theme.palette.gray[100]}`,
                paddingY: '12px',
                paddingRight: '16px'
              }}
            />
          ))}
        </Stack>
        <Button
          variant="text"
          sx={{
            display: 'flex',
            marginX: 'auto',
            color: theme.palette.gray[900]
          }}
          onClick={() => {
            const appStoreId = currentAppStore.storeId;

            setAppStorePostConditions(appStoreId, baseAppStorePostFilter, 'LATEST');

            router.push(REVIEW_ROUTES.getReviews(appStoreId));
          }}
        >
          전체 보기
        </Button>
      </>
    );
  }
};

export default LatestReviews;
